import { Dialog } from '@base-ui/react/dialog'
import type { FC, HTMLAttributes } from 'react'
import { useRef } from 'react'
import backdropStyles from './backdrop.module.css'
import styles from './dialog.module.css'

const maxWidthMap = {
  xs: styles.maxWidthXs,
  sm: styles.maxWidthSm,
  md: styles.maxWidthMd,
  lg: styles.maxWidthLg,
  xl: styles.maxWidthXl,
} as const

/**
 * TunaDialog is controlled (no Dialog.Trigger), so Base UI cannot tell how it was opened and always focuses the
 * first tabbable element — after a tap that pops the on-screen keyboard over the dialog. Track the last input
 * modality ourselves and, after a touch, focus the popup itself, as Base UI does for trigger-opened dialogs.
 */
let lastInputWasTouch = false

if (typeof document !== 'undefined') {
  document.addEventListener(
    'pointerdown',
    event => {
      lastInputWasTouch = event.pointerType === 'touch'
    },
    true
  )
  document.addEventListener(
    'keydown',
    () => {
      lastInputWasTouch = false
    },
    true
  )
}

export interface TunaDialogProps {
  open: boolean
  onClose?: () => void
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
  fullWidth?: boolean
  fullScreen?: boolean
  className?: string
  style?: React.CSSProperties
  /** Id of the element that titles the dialog — gives the dialog its accessible name */
  'aria-labelledby'?: string
  'aria-label'?: string
  /** The dialog is always modal; set this where assistive tech must be told so (bottom sheets). */
  'aria-modal'?: boolean
  /**
   * The element that takes focus on open, instead of the first tabbable one (which, with a close button in
   * the title, is that button). Ignored after touch input, where focusing a field would pop the keyboard.
   */
  initialFocus?: React.RefObject<HTMLElement | null>
  children?: React.ReactNode
}

export const TunaDialog: FC<TunaDialogProps> = ({
  open,
  onClose,
  maxWidth = 'sm',
  fullWidth,
  fullScreen,
  className,
  children,
  style,
  'aria-labelledby': ariaLabelledBy,
  'aria-label': ariaLabel,
  'aria-modal': ariaModal,
  initialFocus,
}) => {
  const popupRef = useRef<HTMLDivElement>(null)

  const dialogClasses = [
    styles.dialog,
    fullScreen ? styles.fullScreen : undefined,
    maxWidth === false ? styles.maxWidthNone : maxWidth && !fullScreen ? maxWidthMap[maxWidth] : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <Dialog.Root
      open={open}
      onOpenChange={o => {
        if (!o) {
          onClose?.()
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className={backdropStyles.backdrop} />
        <Dialog.Popup
          ref={popupRef}
          initialFocus={() => (lastInputWasTouch ? popupRef.current : (initialFocus?.current ?? true))}
          className={dialogClasses}
          aria-labelledby={ariaLabelledBy}
          aria-label={ariaLabel}
          aria-modal={ariaModal}
          style={fullWidth ? { width: '100%', ...style } : style}
        >
          {children}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export interface DialogTitleProps extends HTMLAttributes<HTMLHeadingElement> {}

export const DialogTitle: FC<DialogTitleProps> = ({ className, ...rest }) => (
  <h2 className={[styles.title, className].filter(Boolean).join(' ')} {...rest} />
)

export interface DialogContentProps extends HTMLAttributes<HTMLDivElement> {
  dividers?: boolean
}

export const DialogContent: FC<DialogContentProps> = ({ dividers, className, style, ...rest }) => (
  <div
    className={[styles.content, className].filter(Boolean).join(' ')}
    style={
      dividers
        ? {
            borderTop: '1px solid var(--tuna-palette-divider)',
            borderBottom: '1px solid var(--tuna-palette-divider)',
            ...style,
          }
        : style
    }
    {...rest}
  />
)

export interface DialogActionsProps extends HTMLAttributes<HTMLDivElement> {}

export const DialogActions: FC<DialogActionsProps> = ({ className, ...rest }) => (
  <div className={[styles.actions, className].filter(Boolean).join(' ')} {...rest} />
)
